import { computed, action, observable } from 'mobx';
import { ViewController } from '@estee/elc-service';
import { NumberFormatter } from '@estee/elc-formatters';
import { LoyaltyRepository, offerRedemptionType } from '~repositories/LoyaltyRepository';
import { emit, events } from '@estee/elc-service-bus';
import { ILoyaltyRPCRequest } from '~api/interfaces/ILoyaltyRpcAPI';
import { diContainer } from '~setup/diContainer';
import serviceNames from '~setup/LoyaltyService';
import { IBirthdayContent } from './ILoyaltyBirthday';
import { AnalyticalTrackingUseCase } from '~use-cases/AnalyticalTrackingUseCase';
import { eventTag } from '~utils/Analytics';
import { updateCartEvent } from '~internal/utils/CartUtils';

export interface IConfigParamsNumberFormats {
    groupingFormatForComma?: boolean;
    birthday: IBirthdayContent;
}

class LoyaltyBirthdayController extends ViewController {
    private loyaltyRepository: LoyaltyRepository;
    private numberFormatter: NumberFormatter;

    @observable currentTabIndex: string;
    @observable private configParams: IConfigParamsNumberFormats;
    @observable rewardOfferType: string;
    @observable isViewDetailsModalOpen: boolean = false;

    constructor(loyaltyRepository: LoyaltyRepository) {
        super();
        this.loyaltyRepository = loyaltyRepository;
        this.numberFormatter = diContainer.get(serviceNames.numberFormatter);
    }

    @action
    public setConfigParams = (configParams: IConfigParamsNumberFormats) => {
        this.configParams = configParams;
    };

    @action
    public loaded = async (rewardOfferType?: string) => {
        if (rewardOfferType) {
            this.rewardOfferType = rewardOfferType;
            this.isLoading = false;
        }
    };

    public onShopNowLinkClick = (linkLabel: string) => {
        emit(events.LOYALTY_SHOP_NOW_CLICKED, linkLabel);
    };

    @action
    public handleRedeemOffer = async (applyOfferCode: ILoyaltyRPCRequest) => {
        this.isLoading = true;
        await this.loyaltyRepository.redeemOffer(
            applyOfferCode,
            this.rewardOfferType,
            offerRedemptionType.birthday
        );

        const birthdayMessageData = this.loyaltyRepository?.offerRedemptionMessageData?.birthday;
        const offerSuccessMessageFromConfig = this.configParams?.birthday?.offerSuccessMessage?.text;
        if (
            offerSuccessMessageFromConfig &&
            birthdayMessageData?.type === 'success' &&
            birthdayMessageData?.redemptionAction === 'applied'
        ) {
            this.loyaltyRepository.setOfferRedemptionMessage({
                ...birthdayMessageData,
                content: offerSuccessMessageFromConfig
            });
        }

        const { offerView } = applyOfferCode;
        if (offerView === 'ChoicesOfSku') {
            const cartItemcCount = this.loyaltyRepository?.benefitsOfferInfo?.cartItemsCount;
            if (cartItemcCount) {
                emit(events.LOYALTY_REWARDS_REDEEM_CLICKED);
                // This line will be uncommented once cart overlay repo rendered on vulcan page
                // await command(serviceCommands.CART_OVERLAY_SHOW);
            }
        }

        updateCartEvent();
        this.isLoading = false;
    };

    @action
    public handleRemoveOffer = async (offerCode: string, offerView: string) => {
        this.isLoading = true;
        await this.loyaltyRepository.removeOffer(
            offerCode,
            this.rewardOfferType,
            offerRedemptionType.birthday
        );

        const birthdayMessageData = this.loyaltyRepository?.offerRedemptionMessageData?.birthday;
        const offerRemovedMessageFromConfig = this.configParams?.birthday?.offerRemovedMessage?.text;
        if (
            offerRemovedMessageFromConfig &&
            birthdayMessageData?.type === 'success' &&
            birthdayMessageData?.redemptionAction === 'removed'
        ) {
            this.loyaltyRepository.setOfferRedemptionMessage({
                ...birthdayMessageData,
                content: offerRemovedMessageFromConfig
            });
        }

        if (offerView === 'ChoicesOfSku') {
            emit(events.LOYALTY_REWARDS_REMOVE_CLICKED);
        }

        updateCartEvent();
        this.isLoading = false;
    };

    @action
    public setCurrentTabId = (tabId: string) => {
        this.currentTabIndex = tabId;

        emit(events.LOYALTY_REWARDS_TAB_CLICKED, tabId);
    };

    @action
    public onRewardsArrowClick(eLabel: string) {
        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            { event_name: 'loyalty_rewards', event_action: 'arrow_click', event_label: eLabel },
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();
    }

    @action
    public onRewardViewDetailsClick(eLabel: string) {
        const LoyaltyEventTracking = new AnalyticalTrackingUseCase(
            { event_name: 'loyalty_rewards', event_action: 'cta_click', event_label: eLabel },
            eventTag.LOYALTY_ACTIONS_TRACKING
        );
        LoyaltyEventTracking.execute();
    }

    @computed
    get currentTabId() {
        return this.currentTabIndex;
    }

    @action getFilteredBirthdayOffers(productFilter: string) {
        const birthdayRewards = this.loyaltyRepository?.birthdayRewardsInfo?.rewardsDataFormatted || [];

        return birthdayRewards.filter((offer) => {
            return offer.path ? offer.path.indexOf(productFilter) > -1 : true;
        });
    }

    @computed
    public get formattedPointsValue() {
        const { totalRedeemedPoints = 0 } = this.loyaltyRepository?.rewardsInfo || {};
        const pointsValue =
            this.loyaltyRepository?.loyaltyInfo?.currentPoints - totalRedeemedPoints;

        const { groupingFormatForComma } = this.configs;

        return this.numberFormatter.formatNumber(pointsValue, {
            shouldUseGrouping: groupingFormatForComma
        });
    }

    @computed
    public get daysLeft() {
        const currentDate = new Date();

        if (this.loyaltyRepository?.birthdayRewardsInfo?.rewardsDataFormatted?.length) {
            const criteriasArr = this.loyaltyRepository?.birthdayRewardsInfo?.rewardsDataFormatted.flatMap(rdf => rdf.criteria);
            const sinceCriteria = criteriasArr.find(c => c.class === 'Since' && c.since === 'birthday');
            if (sinceCriteria) {
                const nDays = Number(sinceCriteria.num_days);
                if (!isNaN(nDays)) {
                    const maxDate = new Date(
                        currentDate.getFullYear(),
                        this.loyaltyRepository?.loyaltyInfo.birthMonth - 1,
                        this.loyaltyRepository?.loyaltyInfo.birthDay
                    );
                    maxDate.setDate(maxDate.getDate() + nDays);
                    const normalizedCurrentDate = new Date(
                        currentDate.getFullYear(),
                        currentDate.getMonth(),
                        currentDate.getDate()
                    );
                    const diff = (maxDate.getTime() - normalizedCurrentDate.getTime()) / (1000 * 60 * 60 * 24);

                    return String(diff);
                }
            }
        }

        const lastDay = new Date(
                currentDate.getFullYear(),
                currentDate.getMonth() + 1,
                0
            ).getDate();

        return String(lastDay - currentDate.getDate());
    }

    @action
    private replaceTextWithDaysLeft(text: string, daysLeft: string): string {
        const daysLeftText = '::days::';

        return text.replace(daysLeftText, String(daysLeft));
    }

    @action
    private replaceTextWithName(text: string, name: string): string {
        const nameText = '::name::';

        return text.replace(nameText, name);
    }

    @computed
    private get customerName(): string {
        const { firstName, lastName } = this.loyaltyRepository?.loyaltyInfo || {};

        return firstName || lastName || '';
    }

    @action
    private replaceGreetingsTextWithDaysLeft(text: string, daysLeft: string): string {
        const daysLeftText = '::days::';

        return text.replace(daysLeftText, String(daysLeft));
    }

    @action
    public toggleViewDetailsModal = () => {
        this.isViewDetailsModalOpen = !this.isViewDetailsModalOpen;
    };

    @computed
    public get formattedBirthdayHeader(): string {
        const { birthdayBannerHeader } = this.configs.birthday;
        const headerText = birthdayBannerHeader?.text || '';
        const name = this.customerName.length <= 15 ? this.customerName : this.loyaltyRepository?.loyaltyInfo?.currentTierName;

        return headerText.includes('::name::')
            ? (name ? this.replaceTextWithName(headerText, name) : headerText.replace(', ::name::', ''))
            : headerText;
    }

    @computed
    public get formattedBirthdayGreetings(): string {
        const { birthdayBannerGreetings } = this.configs.birthday;
        const greetingsText = birthdayBannerGreetings?.text || '';

        return this.replaceGreetingsTextWithDaysLeft(greetingsText, this.daysLeft);
    }

    @computed
    public get loyaltyLevelCurrent(): string {
        const loyaltyInfo = this.loyaltyRepository?.loyaltyInfo;
        const levelValue = loyaltyInfo?.levelValue;
        const {
            birthdayDaysLeft,
            birthdayDaysLeftTwo,
            birthdayDaysLeftThree,
            birthdayDaysLeftFour
        } = this.configs.birthday;

        switch (levelValue) {
            case 1:
                return this.replaceTextWithDaysLeft(birthdayDaysLeft?.text ? birthdayDaysLeft.text : '', this.daysLeft);
            case 2:
                return this.replaceTextWithDaysLeft(birthdayDaysLeftTwo?.text ? birthdayDaysLeftTwo.text : '', this.daysLeft);
            case 3:
                return this.replaceTextWithDaysLeft(birthdayDaysLeftThree?.text ? birthdayDaysLeftThree.text : '', this.daysLeft);
            case 4:
                return this.replaceTextWithDaysLeft(birthdayDaysLeftFour?.text ? birthdayDaysLeftFour.text : '', this.daysLeft);
            default:
                return this.replaceTextWithDaysLeft(birthdayDaysLeft?.text ? birthdayDaysLeft.text : '', this.daysLeft);
        }
    }

    @computed
    public get birthdayInstructions(): string {
        const loyaltyInfo = this.loyaltyRepository?.loyaltyInfo;
        const levelValue = loyaltyInfo?.levelValue;
        const {
            birthdayInstructions,
            birthdayInstructionsTwo,
            birthdayInstructionsThree,
            birthdayInstructionsFour
        } = this.configs.birthday;

        switch (levelValue) {
            case 1:
                return birthdayInstructions?.text ? birthdayInstructions.text : '';
            case 2:
                return birthdayInstructionsTwo?.text ? birthdayInstructionsTwo.text : '';
            case 3:
                return birthdayInstructionsThree?.text ? birthdayInstructionsThree.text : '';
            case 4:
                return birthdayInstructionsFour?.text ? birthdayInstructionsFour.text : '';
            default:
                return birthdayInstructions?.text ? birthdayInstructions.text : '';
        }
    }

    @computed
    public get data() {
        return {
            rewardsInfo: this.loyaltyRepository?.birthdayRewardsInfo?.rewardsDataFormatted,
            rewardsOfferInfo: this.loyaltyRepository?.benefitsOfferInfo,
            hasEligibleBirthdayOffers: this.loyaltyRepository?.hasEligibleBirthdayOffers,
            loyaltyLevelCurrent: this.loyaltyLevelCurrent,
            rewardRedeemed: this.loyaltyRepository?.isRewardRedeemed,
            offerRedemptionMessages: this.loyaltyRepository?.offerRedemptionMessageData,
            isLoading: this.isLoading
        };
    }

    @computed
    public get configs() {
        return this.configParams;
    }
}

export default LoyaltyBirthdayController;
